import { useState } from 'react';
import { hashPassword } from '@/lib/hashUtils';

const EDGE_URL = 'https://pdxhoigcrxsqwxgrphdv.supabase.co/functions/v1/custom-auth';

interface AddMemberModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

const AddMemberModal = ({ onClose, onSuccess }: AddMemberModalProps) => {
  const [form, setForm] = useState({
    username: '',
    password: '',
    nickname: '',
    email: '',
    phone: '',
    inviteCode: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showPwd, setShowPwd] = useState(false);

  const handleSubmit = async () => {
    if (!form.username.trim()) { setError('请输入用户名'); return; }
    if (form.password.length < 6) { setError('密码至少6位'); return; }
    if (!form.nickname.trim()) { setError('请输入昵称'); return; }
    if (!form.phone.trim() || !/^1[3-9]\d{9}$/.test(form.phone.trim())) {
      setError('请输入正确的手机号'); return;
    }
    if (form.email.trim()) {
      const emailReg = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailReg.test(form.email.trim())) { setError('邮箱格式不正确'); return; }
    }

    setSubmitting(true);
    setError('');

    try {
      // 通过云函数注册，确保 level 正确设置为「普通用户」
      const res = await fetch(EDGE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'register',
          username: form.username.trim(),
          password: form.password,
          nickname: form.nickname.trim(),
          phone: form.phone.trim(),
          email: form.email.trim() || undefined,
          inviteCode: form.inviteCode.trim() || undefined,
        }),
      });

      const data = await res.json();

      if (data.error) {
        setError(data.error);
        setSubmitting(false);
        return;
      }

      onSuccess();
    } catch {
      setError('网络错误，请重试');
    }

    setSubmitting(false);
  };

  // ... existing code ...
  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-50" onClick={() => !submitting && onClose()} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className="bg-white rounded-2xl w-full max-w-md p-6"
          style={{ animation: 'popIn 0.25s cubic-bezier(0.34,1.56,0.64,1)' }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center flex-shrink-0">
              <i className="ri-user-add-line text-emerald-500 text-xl" />
            </div>
            <div>
              <h3 className="text-base font-bold text-gray-900">新增会员</h3>
              <p className="text-xs text-gray-400">手动创建会员账号（初始等级：普通用户）</p>
            </div>
          </div>

          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">用户名 *</label>
                <input
                  type="text"
                  value={form.username}
                  onChange={(e) => { setForm(p => ({ ...p, username: e.target.value })); setError(''); }}
                  placeholder="登录用户名"
                  maxLength={30}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-emerald-400"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">昵称 *</label>
                <input
                  type="text"
                  value={form.nickname}
                  onChange={(e) => { setForm(p => ({ ...p, nickname: e.target.value })); setError(''); }}
                  placeholder="显示昵称"
                  maxLength={20}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-emerald-400"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">登录密码 *</label>
              <div className="relative">
                <input
                  type={showPwd ? 'text' : 'password'}
                  value={form.password}
                  onChange={(e) => { setForm(p => ({ ...p, password: e.target.value })); setError(''); }}
                  placeholder="至少6位"
                  maxLength={32}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-emerald-400 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd(!showPwd)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 cursor-pointer"
                >
                  <i className={showPwd ? 'ri-eye-line' : 'ri-eye-off-line'} />
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                手机号 * <span className="text-xs text-gray-400 font-normal">（会员登录使用）</span>
              </label>
              <input
                type="tel"
                value={form.phone}
                onChange={(e) => { setForm(p => ({ ...p, phone: e.target.value })); setError(''); }}
                placeholder="请输入手机号"
                maxLength={11}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-emerald-400"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                邮箱 <span className="text-xs text-gray-400 font-normal">（选填）</span>
              </label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => { setForm(p => ({ ...p, email: e.target.value })); setError(''); }}
                placeholder="选填"
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-emerald-400"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                邀请码 <span className="text-xs text-gray-400 font-normal">（选填，填写后自动绑定上级）</span>
              </label>
              <input
                type="text"
                value={form.inviteCode}
                onChange={(e) => { setForm(p => ({ ...p, inviteCode: e.target.value.toUpperCase() })); setError(''); }}
                placeholder="FZY开头的邀请码"
                maxLength={12}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-emerald-400 uppercase"
              />
            </div>

            <div className="bg-amber-50 rounded-xl px-3 py-2.5 flex items-start gap-2">
              <i className="ri-information-line text-amber-500 text-sm mt-0.5 flex-shrink-0" />
              <p className="text-xs text-amber-700 leading-relaxed">
                新会员初始等级为「普通用户」，积分和余额可在创建后通过「调整积分」「调整余额」功能单独操作。
              </p>
            </div>
          </div>

          {error && (
            <div className="mt-3 flex items-center gap-2 bg-rose-50 text-rose-500 text-sm px-3 py-2.5 rounded-xl">
              <i className="ri-error-warning-line flex-shrink-0" />{error}
            </div>
          )}

          <div className="flex gap-3 mt-5">
            <button
              onClick={onClose}
              disabled={submitting}
              className="flex-1 border border-gray-200 text-gray-600 py-2.5 rounded-xl text-sm cursor-pointer whitespace-nowrap hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              取消
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="flex-1 bg-emerald-500 text-white py-2.5 rounded-xl text-sm font-medium cursor-pointer whitespace-nowrap hover:bg-emerald-600 transition-colors disabled:opacity-50"
            >
              {submitting ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  创建中...
                </span>
              ) : (
                <span><i className="ri-user-add-line mr-1" />确认创建</span>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddMemberModal;
